/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { IpGroupModel } from '../models';
import { IpGroupsEnum } from '../enums/api';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber } from 'rxjs';

/**
 * Network Service
 * Note: This class is to be used to make API calls
 *       and return with a structured response.
 */
@Injectable()
export class IpGroupApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get all network groups
     * @returns {Observable<IpGroupModel[]>}
     */
    public getGroups(options?: any): Observable<IpGroupModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.api.get(IpGroupsEnum.GET_GROUPS_ALL, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add an ip group.
     * @param data
     * @param options
     */
    public addGroup(data: IpGroupModel, options?: any): Observable<IpGroupModel> {
        return new Observable((observer: Subscriber<any>): void => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.api.post(IpGroupsEnum.POST_GROUP, data, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Edit an ip group.
     * @param data
     * @param options
     */
    public editGroup(data: IpGroupModel, options?: any): Observable<IpGroupModel> {
        return new Observable((observer: Subscriber<any>): void => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.api.put(IpGroupsEnum.PUT_GROUP(data.id), data, moduleAuth)
                    .subscribe({
                        next: (response: IpGroupModel): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Remove an ip group.
     * @param id
     * @param options
     */
    public deleteGroup(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.api.delete(IpGroupsEnum.DELETE_GROUP(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }
}
